// `(distinct ds [:a :b …])` — keep rows whose combination of the listed
// column values is unique. Top-level eager op consumable as
// `(distinct ds [:a :b])` directly or via `(-> ds (distinct [:a :b]))`.

import { dedupeByKeys } from '../../runtime/partition.ts';
import { buildDsColumnListOp } from './helpers.ts';

/**
 * `(distinct ds [:name :other …])` — keep the first row of each
 * unique combination of the listed column values. Each name is a
 * keyword (typical) or a string (special-character fallback). Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - A listed name isn't a column on `ds.schema` →
 *     `UnknownColumnRefError` (with the dataset's columns as the
 *     "did you mean").
 *
 * Uniqueness is structural via {@link partitionByKeys} / `cellKey`:
 * struct cells (e.g. CDF `{space, externalId}` instance refs) collapse
 * by content, not by JS reference identity. A `LazyDataset` input is
 * forced via `ctx.materialize` before the partition runs — `distinct`
 * produces a `MaterializedDataset` either way.
 */
export const distinct = buildDsColumnListOp({
  name: 'distinct',
  describe:
    'Keep rows whose combination of the listed column values is unique. Names are keywords ' +
    '(typical) or strings (arbitrary characters). Composes with threading: `(-> ds (distinct [:a :b]))`.',
  example: { expr: '(-> wells (distinct [:region :year]))' },
  verb: (ds, names) => dedupeByKeys(ds, names),
  // Row deduplication only: columns and types unchanged.
  propagateSchema: (_names, ctx) => ctx.inputSchema,
});
